import type { TagEntity } from '@experiences/interfaces';
import {
    ExecutableType,
    GovernanceResourceType,
} from '@experiences/interfaces';
import type { IApAutocompleteItem } from '@uipath/portal-shell/loader';

import type { EntitySubType } from '../../../../../services/resource-catalog/ResourceCatalogService';

export const RESOURCE_TYPE_TO_ENTITY_SUBTYPE: Record<GovernanceResourceType, EntitySubType> = {
    [GovernanceResourceType.Agent]: 'agent',
    [GovernanceResourceType.AgenticProcess]: 'processorchestration',
    [GovernanceResourceType.RPAWorkflow]: 'process',
    [GovernanceResourceType.APIWorkflow]: 'api',
    [GovernanceResourceType.CaseManagement]: 'caseManagement',
    [GovernanceResourceType.Flow]: 'flow',
};

export const EXECUTABLE_TYPE_TO_ENTITY_SUBTYPE: Record<ExecutableType, EntitySubType> = {
    [ExecutableType.Agent]: 'agent',
    [ExecutableType.AgenticProcess]: 'processorchestration',
    [ExecutableType.CaseManagement]: 'caseManagement',
    [ExecutableType.Flow]: 'flow',
};

/**
 * Collect all concrete entity keys (excluding wildcard-only "*")
 * from selectors and executable rules.
 */
export const collectEntityKeysFromSelectorsAndExecutableRule = (
    selectors?: Array<{ values?: string[] }>,
    executableRule?: { values?: Array<{ values?: string[] }> },
): string[] => {
    const keys = new Set<string>();

    (selectors ?? []).forEach((selector) => {
        const selectorValues = selector?.values ?? [];
        if (selectorValues.length === 0) {
            return;
        }

        const isAll = selectorValues.length === 1 && selectorValues[0] === '*';
        if (isAll) {
            return;
        }

        selectorValues.forEach((v: string) => {
            if (v) {
                keys.add(v);
            }
        });
    });

    (executableRule?.values ?? []).forEach((value) => {
        const valueItems = value?.values ?? [];
        if (valueItems.length === 0) {
            return;
        }

        const isAll = valueItems.length === 1 && valueItems[0] === '*';
        if (isAll) {
            return;
        }

        valueItems.forEach((v: string) => {
            if (v) {
                keys.add(v);
            }
        });
    });

    return Array.from(keys);
};

/**
 * Map a Resource Catalog processes record to autocomplete items
 * used by ApAutocompleteInput.
 */
export const mapProcessesToAutocompleteItems = (
    processes: Record<string, any>,
): IApAutocompleteItem[] => Object.entries(processes).map(([ id, process ]) => ({
    id,
    title: process.name,
    subtitle: process.metadata?.folderPath ?? process.metadata?.folderName ?? '',
    data: process,
    // state: 'invalid',
}));

export const mapTagsToAutocompleteItems = (tags: TagEntity[]): IApAutocompleteItem[] =>
    tags.map((tag) => ({
        id: tag.name,
        title: tag.name,
    } as IApAutocompleteItem));

/**
 * Check if any items in an array have invalid state.
 * Used by both PolicyAutomationRuleViewModel and PolicyResourceRuleViewModel.
 */
export const hasInvalidChips = (items: IApAutocompleteItem[]): boolean =>
    items.some((item) => item.state === 'invalid');

/**
 * Collect entity keys from values array, excluding wildcard "*".
 * Used for hydration purposes.
 */
export const collectEntityKeys = (values: any[]): string[] => {
    const entityKeysSet = new Set<string>();
    values.forEach((val: any) => {
        const id = typeof val === 'string'
            ? val
            : (val.id ?? val.value ?? val.primaryText);

        if (id && id !== '*') {
            entityKeysSet.add(id);
        }
    });
    return Array.from(entityKeysSet);
};
